import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

const STATS = [
  { value: "100+", label: "Clients Advised" },
  { value: "7+", label: "Years of Experience" },
  { value: "80+", label: "Early-Career Professionals" },
  { value: "30+", label: "Career Pivots Guided" },
];

const CREDENTIALS = [
  { label: "Education", value: "Bachelor of Arts in Philosophy, University of Toronto — Ethics, Decision-Making & Human Reasoning" },
  { label: "Background", value: "1:1 consulting, private tutoring, and strategic advisory across interview preparation, resume writing, career repositioning, direction consulting, and career-aligned academic applications." },
  { label: "Specialization", value: "Career direction, professional repositioning, interview strategy, resume development, applications advising, and structured support through periods of transition." },
];

export default function AboutPage() {
  return (
    <div className="bg-ivory min-h-screen">
      {/* Header */}
      <section className="bg-navy py-24 px-6 text-center">
        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }}
          className="font-lora text-xs tracking-[0.3em] uppercase text-gold mb-4"
        >
          About
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}
          className="font-lora text-4xl lg:text-5xl text-ivory font-normal"
        >
          De Nicola Consulting Co.
        </motion.h1>
        <div className="w-12 h-px bg-gold mx-auto mt-6"></div>
        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.4 }}
          className="text-ivory/60 font-serif text-base mt-5 max-w-xl mx-auto leading-relaxed"
        >
          Strategic advisory grounded in clarity, philosophy, and a genuine commitment to helping individuals move forward with intention.
        </motion.p>
      </section>

      {/* Meet the Founder */}
      <section className="py-24 px-6 lg:px-12 bg-white" data-testid="founder-section">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-5 gap-16 items-start">
            {/* Photo Column */}
            <motion.div
              className="lg:col-span-2"
              initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.9 }}
            >
              <div className="relative inline-block w-full max-w-sm mx-auto">
                <div className="absolute -top-3 -left-3 w-full h-full border border-gold/30 rounded-sm pointer-events-none"></div>
                <img
                  src="https://customer-assets.emergentagent.com/job_beyond-stuck/artifacts/08b71y1p_Head%20Shot.jpg"
                  alt="Julia De Nicola — Founder, De Nicola Consulting Co."
                  className="w-full object-cover object-top rounded-sm relative z-10 shadow-xl"
                  data-testid="founder-headshot"
                />
              </div>

              {/* Credentials block */}
              <div className="mt-8 space-y-4">
                {CREDENTIALS.map((c) => (
                  <div key={c.label} className="border-l-2 border-gold pl-4">
                    <p className="font-lora text-xs tracking-[0.15em] uppercase text-gold mb-1">{c.label}</p>
                    <p className="text-navy/60 font-serif text-sm leading-relaxed">{c.value}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Bio Column */}
            <motion.div
              className="lg:col-span-3"
              initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.9, delay: 0.2 }}
            >
              <p className="font-lora text-xs tracking-[0.3em] uppercase text-gold mb-3">Meet the Founder</p>
              <h2 className="font-lora text-4xl text-navy font-normal mb-1">Julia De Nicola</h2>
              <p className="font-lora text-base text-navy/50 italic mb-6">Founder, De Nicola Consulting Co.</p>
              <div className="w-10 h-px bg-gold mb-8"></div>

              <div className="space-y-5 text-navy/70 font-serif leading-relaxed">
                <p>
                  Julia De Nicola's work is grounded in a simple but often overlooked reality: many capable individuals are not held back by a lack of ability, but by a lack of clarity.
                </p>
                <p>
                  She holds a Bachelor of Arts in Philosophy from the University of Toronto, where her studies focused on ethics, decision-making, and the structure of human reasoning. This foundation continues to shape her approach — helping individuals step back, understand their position, and move forward with greater intention.
                </p>
                <p>
                  Her work in litigation environments further developed this perspective. In settings where precision matters and outcomes carry weight, she learned to approach problems with structure, discipline, and careful judgment — skills that now inform how she guides individuals through uncertain and often high-stakes career moments.
                </p>
                <p>
                  Over the past 7+ years, Julia has mentored and advised students and professionals across a wide range of transitions — from early academic decisions to career pivots, professional repositioning, and re-entry into higher education.
                </p>
                <p>
                  Her approach has also been shaped through professional training in communication, interview strategy, and public engagement, developed during her time at the Legislative Assembly of Ontario. This training emphasized not only how to communicate effectively, but how to do so with clarity, composure, and confidence in high-responsibility environments.
                </p>
                <p>
                  Across her work, a consistent pattern has emerged: people are often closer to their next step than they think — they simply haven't yet been able to see it clearly.
                </p>
                <p>
                  Her work is not centered on isolated tasks, but on strategic positioning: helping individuals understand where they stand, consider where they can go, and move forward in a way that feels grounded, intentional, and aligned.
                </p>
                <p className="font-lora italic text-navy/80 text-lg border-l-4 border-gold pl-5">
                  "Clarity does not always arrive as certainty. More often, it begins as the moment someone finally understands where they stand — and sees, with greater honesty, where they can go next."
                </p>
              </div>

              <div className="mt-10">
                <Link
                  to="/booking"
                  className="inline-flex items-center gap-2 bg-navy text-ivory px-8 py-3.5 text-sm tracking-[0.15em] uppercase hover:bg-navy/90 transition-all group"
                  data-testid="about-book-btn"
                >
                  Schedule a Consultation
                  <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Impact Numbers */}
      <section className="py-20 px-6 lg:px-12 bg-navy">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-8 text-center">
            {STATS.map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.1 }}
              >
                <p className="font-lora text-4xl text-gold font-normal mb-2">{stat.value}</p>
                <p className="text-ivory/60 text-xs tracking-[0.15em] uppercase font-serif">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Experience Detail */}
      <section className="py-24 px-6 lg:px-12 bg-ivory">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-14">
            <p className="font-lora text-xs tracking-[0.3em] uppercase text-gold mb-4">Experience</p>
            <h2 className="font-lora text-3xl text-navy font-normal">Who Julia Has Worked With</h2>
            <div className="w-10 h-px bg-gold mx-auto mt-5"></div>
          </div>
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              {
                count: "80+",
                title: "Early-Career Professionals",
                body: "Individuals navigating early-career direction and first major professional decisions — finding their footing and building a strategy for what comes next.",
              },
              {
                count: "30+",
                title: "Career Pivoters & Repositioners",
                body: "Working professionals seeking to pivot, reposition, or realign within their fields — moving from where they are to where they know they should be.",
              },
              {
                count: "20+",
                title: "Competitive Applicants",
                body: "Individuals pursuing competitive academic pathways as part of a broader career transition, including graduate school and professional programs.",
              },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: i * 0.15 }}
                className="bg-white border border-soft-gray p-7"
              >
                <p className="font-lora text-3xl text-gold mb-3">{item.count}</p>
                <h3 className="font-lora text-navy text-base font-normal mb-3">{item.title}</h3>
                <p className="text-navy/60 font-serif text-sm leading-relaxed">{item.body}</p>
              </motion.div>
            ))}
          </div>
          <div className="mt-10 bg-[#F4F3EF] border border-soft-gray p-7">
            <p className="text-navy/70 font-serif text-sm leading-relaxed text-center">
              Julia has also supported dozens of clients through periods of uncertainty — whether following layoffs, stalled career progression, or the realization that a once-intended path no longer aligned with their strengths or long-term direction. In parallel, she has guided <strong>20+ adult learners</strong> in returning to higher education, helping them approach applications as a strategic step toward more aligned and higher-impact roles.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 px-6 lg:px-12 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="font-lora text-xs tracking-[0.3em] uppercase text-gold mb-4">Guiding Principles</p>
            <h2 className="font-lora text-3xl text-navy font-normal">What Guides This Work</h2>
            <div className="w-10 h-px bg-gold mx-auto mt-6"></div>
          </div>
          <div className="grid sm:grid-cols-3 gap-10">
            {[
              {
                title: "Philosophical Grounding",
                body: "Julia's academic training in ethics and human reasoning shapes every engagement. Before strategy, there must be understanding — of who you are, what you want, and where you actually stand.",
              },
              {
                title: "Structural Precision",
                body: "Drawn from litigation and high-responsibility environments, her approach is disciplined and careful. Every session produces clarity, not generalities — actionable insight you can move on immediately.",
              },
              {
                title: "Genuine Human Care",
                body: "This is structured, high-touch advisory. Julia invests in each client's situation with care and attention, recognizing that career decisions are deeply personal and deserve to be approached with clarity, thoughtfulness, and precision.",
              },
            ].map((v, i) => (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: i * 0.15 }}
                className="text-center px-4"
              >
                <div className="w-10 h-px bg-gold mx-auto mb-5"></div>
                <h3 className="font-lora text-xl text-navy font-normal mb-3">{v.title}</h3>
                <p className="text-navy/60 font-serif text-sm leading-relaxed">{v.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 bg-navy text-center">
        <p className="font-lora text-xs tracking-[0.3em] uppercase text-gold mb-4">Ready to Begin?</p>
        <h2 className="font-lora text-3xl text-ivory font-normal mb-4">
          From Uncertainty to Understanding
        </h2>
        <div className="w-10 h-px bg-gold mx-auto mb-6"></div>
        <p className="text-ivory/60 font-serif text-sm max-w-md mx-auto mb-8 leading-relaxed">
          This practice exists to support the shift from confusion to clarity — and from clarity to confident, intentional movement forward.
        </p>
        <Link
          to="/booking"
          className="inline-flex items-center gap-2 bg-gold text-navy px-10 py-4 text-sm tracking-[0.15em] uppercase hover:bg-gold/90 transition-all group"
        >
          Book Your Session <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
        </Link>
      </section>
    </div>
  );
}
